import { useNavigate, useSearch } from "@tanstack/react-router";

import {
  useCreatePlantProfile,
  usePlantProfile,
} from "@/lib/api/plant-profiles";
import { newULID } from "@/lib/api/client";
import { ErrorState } from "@/components/error-state";

import {
  PlantProfileForm,
  emptyToUndef,
  parseOptNum,
  type PlantProfileFormValues,
} from "./form";

export function PlantProfileNewPage() {
  const navigate = useNavigate();
  const { from } = useSearch({ from: "/chemistry-profile/plant-profiles/new" });
  const create = useCreatePlantProfile();
  // When cloning (`?from=<id>`) pre-fill from the source profile so a read-only
  // system profile becomes an editable tenant copy.
  const source = usePlantProfile(from);

  function submit(values: PlantProfileFormValues) {
    create.mutate(
      {
        id: newULID(),
        name: values.name,
        species: values.species,
        category: emptyToUndef(values.category),
        stage: emptyToUndef(values.stage),
        feeder_class: emptyToUndef(values.feeder_class),
        ph_min: parseOptNum(values.ph_min),
        ph_ideal: parseOptNum(values.ph_ideal),
        ph_max: parseOptNum(values.ph_max),
        ec_min: parseOptNum(values.ec_min),
        ec_ideal: parseOptNum(values.ec_ideal),
        ec_max: parseOptNum(values.ec_max),
        temp_min_c: parseOptNum(values.temp_min_c),
        temp_ideal_c: parseOptNum(values.temp_ideal_c),
        temp_max_c: parseOptNum(values.temp_max_c),
        notes: emptyToUndef(values.notes),
      },
      {
        onSuccess: () =>
          navigate({ to: "/chemistry-profile/plant-profiles" }),
      },
    );
  }

  if (from && source.isLoading)
    return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (from && source.error)
    return <ErrorState error={source.error} retry={source.refetch} />;

  const initial =
    from && source.data
      ? { ...source.data, name: `${source.data.name} (copy)` }
      : undefined;

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold tracking-tight">
        {initial ? "Clone plant profile" : "New plant profile"}
      </h1>
      <PlantProfileForm
        initial={initial}
        submitting={create.isPending}
        onSubmit={submit}
        submitLabel={initial ? "Create copy" : "Create"}
        banner={
          create.error ? (
            <ErrorState error={create.error} retry={() => create.reset()} />
          ) : undefined
        }
      />
    </div>
  );
}
